import { COLORS, COLOR_ORDER, FACE_CONFIG, FACE_ORDER } from './cube-config.js';

export const ORIENTATION_VERSION_4X4 = 1;

const COLOR_VECTOR = Object.freeze(Object.fromEntries(
  FACE_ORDER.map((face) => [FACE_CONFIG[face].color, FACE_CONFIG[face].normal]),
));

export const OPPOSITE_COLOR = Object.freeze(Object.fromEntries(
  FACE_ORDER.map((face) => {
    const oppositeFace = ({ U: 'D', D: 'U', R: 'L', L: 'R', F: 'B', B: 'F' })[face];
    return [FACE_CONFIG[face].color, FACE_CONFIG[oppositeFace].color];
  }),
));

function cross(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function colorForVector(vector) {
  return COLOR_ORDER.find((color) => COLOR_VECTOR[color].every((value, axis) => value === vector[axis]));
}

export function validateOrientation4x4(orientation) {
  const errors = [];
  if (!orientation || !COLOR_ORDER.includes(orientation.top)) {
    errors.push({ code: 'INVALID_TOP_COLOR', message: '请选择有效的顶面颜色' });
  }
  if (!orientation || !COLOR_ORDER.includes(orientation.front)) {
    errors.push({ code: 'INVALID_FRONT_COLOR', message: '请选择有效的正面颜色' });
  }
  if (errors.length) return errors;
  if (orientation.top === orientation.front) {
    errors.push({ code: 'SAME_ORIENTATION_COLORS', message: '顶面与正面不能使用相同颜色' });
  } else if (OPPOSITE_COLOR[orientation.top] === orientation.front) {
    errors.push({ code: 'OPPOSITE_ORIENTATION_COLORS', message: '顶面与正面不能互为相对色' });
  }
  return errors;
}

export function deriveOrientation4x4(orientation) {
  const errors = validateOrientation4x4(orientation);
  if (errors.length) throw new TypeError(errors[0].message);
  const right = colorForVector(cross(COLOR_VECTOR[orientation.top], COLOR_VECTOR[orientation.front]));
  if (!right) throw new TypeError('无法从顶面和正面推导右面颜色');
  const faceToColor = Object.freeze({
    U: orientation.top,
    R: right,
    F: orientation.front,
    D: OPPOSITE_COLOR[orientation.top],
    L: OPPOSITE_COLOR[right],
    B: OPPOSITE_COLOR[orientation.front],
  });
  const colorToFace = Object.freeze(Object.fromEntries(
    Object.entries(faceToColor).map(([face, color]) => [color, face]),
  ));
  return Object.freeze({
    version: ORIENTATION_VERSION_4X4,
    top: orientation.top,
    front: orientation.front,
    faceToColor,
    colorToFace,
  });
}

export function defaultOrientation4x4() {
  return deriveOrientation4x4({ top: 'white', front: 'green' });
}

export function orientationInstruction4x4(orientation) {
  const derived = orientation.faceToColor ? orientation : deriveOrientation4x4(orientation);
  return `请将${COLORS[derived.top].name}面朝上、${COLORS[derived.front].name}面朝向自己，并保持该方向录入。`;
}

export function serializeOrientation4x4(orientation) {
  const derived = orientation.faceToColor ? orientation : deriveOrientation4x4(orientation);
  return JSON.stringify({ version: ORIENTATION_VERSION_4X4, top: derived.top, front: derived.front });
}

export function deserializeOrientation4x4(serialized) {
  const data = typeof serialized === 'string' ? JSON.parse(serialized) : serialized;
  if (!data || data.version !== ORIENTATION_VERSION_4X4) throw new TypeError('4×4 方向数据版本不受支持');
  return deriveOrientation4x4({ top: data.top, front: data.front });
}

